import { Link } from "react-router-dom";
import { useState } from "react";

export default function HeroSection() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    fetch("https://readdy.ai/api/form/d6va45dt4s7j0tv5icu0", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data.toString(),
    }).then(() => {
      setSubmitted(true);
      form.reset();
    });
  };

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/73bbf639274ab4618a4040327b6cc90b.jpg"
          alt="Jerry's Plumbing professional service DFW Texas"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1829]/92 via-[#0B1829]/75 to-[#0B1829]/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1829]/60 via-transparent to-transparent" />
      </div>

      {/* Subtle diagonal texture */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, transparent, transparent 60px, rgba(255,255,255,0.5) 60px, rgba(255,255,255,0.5) 61px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-[110px] pb-16 w-full">
        <div className="grid lg:grid-cols-[1fr_440px] gap-12 items-center">
          {/* Left Content */}
          <div>
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#E8500A]/20 border border-[#E8500A]/40 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 bg-[#E8500A] rounded-full animate-pulse" />
              <span className="text-orange-300 text-xs font-bold tracking-widest uppercase">
                35+ Years Serving Dallas–Fort Worth
              </span>
            </div>

            <h1
              className="text-5xl lg:text-[64px] font-black leading-[1.03] tracking-tight text-white mb-6"
              style={{ fontFamily: "'Barlow', sans-serif" }}
            >
              Reliable Plumbing &amp;{" "}
              <span className="text-[#E8500A]">Heating Services</span>
              <br />
              <span className="text-white">You Can Count On</span>
            </h1>

            <p className="text-gray-300 text-xl leading-relaxed mb-5 max-w-xl">
              Residential &amp; commercial plumbing, heating, and emergency services by licensed Master Plumber professionals who get it done right — the first time.
            </p>

            {/* Phone number prominent */}
            <a
              href="tel:8178324968"
              className="inline-flex items-center gap-2.5 text-white hover:text-orange-300 font-black text-2xl mb-8 transition-colors group"
              style={{ fontFamily: "'Barlow', sans-serif" }}
            >
              <span className="w-10 h-10 flex items-center justify-center bg-[#E8500A] rounded-full group-hover:bg-[#d14508] transition-colors flex-shrink-0">
                <i className="ri-phone-fill text-base" />
              </span>
              (817) 832-4968
              <span className="text-gray-400 text-sm font-normal ml-1">— Call 24/7</span>
            </a>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 mb-10">
              <a
                href="tel:8178324968"
                className="flex items-center gap-2.5 bg-[#E8500A] hover:bg-[#d14508] text-white font-bold px-8 py-4 rounded-lg text-base transition-all hover:scale-[1.02] whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill" />
                </span>
                Call Now — Emergency Line
              </a>
              <Link
                to="/contact"
                className="flex items-center gap-2 border-2 border-white/25 hover:border-white text-white font-bold px-8 py-4 rounded-lg text-base transition-all hover:bg-white/10 whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-check-line" />
                </span>
                Request Service
              </Link>
            </div>

            {/* Stats row */}
            <div className="flex flex-wrap gap-8 pt-8 border-t border-white/10">
              {[
                { value: "35+", label: "Years Experience" },
                { value: "24/7", label: "Emergency Service" },
                { value: "5★", label: "Rated Service" },
                { value: "LIC #M12160", label: "Master Plumber" },
              ].map((stat) => (
                <div key={stat.label}>
                  <p
                    className="text-2xl font-black text-[#E8500A] leading-none"
                    style={{ fontFamily: "'Barlow', sans-serif" }}
                  >
                    {stat.value}
                  </p>
                  <p className="text-gray-400 text-xs font-medium mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right — Estimate Form */}
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl">
            <div className="bg-[#0B1829] px-7 py-5">
              <h2
                className="text-xl font-black text-white"
                style={{ fontFamily: "'Barlow', sans-serif" }}
              >
                Get a Free Estimate
              </h2>
              <p className="text-gray-400 text-xs mt-1">Fast response — usually within the hour</p>
            </div>

            <div className="px-7 py-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-2xl text-green-600" />
                  </div>
                  <h3 className="font-black text-lg text-gray-900 mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
                    Request Sent!
                  </h3>
                  <p className="text-gray-500 text-sm mb-4">
                    We&apos;ll be in touch shortly. For urgent issues, call{" "}
                    <a href="tel:8178324968" className="text-[#E8500A] font-semibold">(817) 832-4968</a>.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-[#E8500A] text-sm font-semibold hover:underline cursor-pointer"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form
                  id="hero-estimate-form"
                  data-readdy-form
                  method="POST"
                  action="https://readdy.ai/api/form/d6va45dt4s7j0tv5icu0"
                  className="space-y-3.5"
                  onSubmit={handleSubmit}
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5" htmlFor="hero-first">
                        First Name <span className="text-[#E8500A]">*</span>
                      </label>
                      <input
                        id="hero-first"
                        name="first_name"
                        type="text"
                        required
                        placeholder="John"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#E8500A] focus:ring-1 focus:ring-[#E8500A]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5" htmlFor="hero-last">
                        Last Name
                      </label>
                      <input
                        id="hero-last"
                        name="last_name"
                        type="text"
                        placeholder="Smith"
                        className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#E8500A] focus:ring-1 focus:ring-[#E8500A]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5" htmlFor="hero-phone">
                      Phone Number <span className="text-[#E8500A]">*</span>
                    </label>
                    <input
                      id="hero-phone"
                      name="phone"
                      type="tel"
                      required
                      placeholder="(817) 555-0123"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#E8500A] focus:ring-1 focus:ring-[#E8500A]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5" htmlFor="hero-email">
                      Email Address <span className="text-[#E8500A]">*</span>
                    </label>
                    <input
                      id="hero-email"
                      name="email"
                      type="email"
                      required
                      placeholder="john@email.com"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#E8500A] focus:ring-1 focus:ring-[#E8500A]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5" htmlFor="hero-service">
                      Service Needed
                    </label>
                    <select
                      id="hero-service"
                      name="service"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-[#E8500A] focus:ring-1 focus:ring-[#E8500A]"
                    >
                      <option value="">Select a service...</option>
                      <option>Plumbing Repair</option>
                      <option>Drain Cleaning</option>
                      <option>Water Heater</option>
                      <option>Slab Leak Detection</option>
                      <option>Grease Trap</option>
                      <option>Welding</option>
                      <option>Emergency Service</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5" htmlFor="hero-message">
                      Describe Your Issue
                    </label>
                    <textarea
                      id="hero-message"
                      name="message"
                      rows={3}
                      maxLength={500}
                      placeholder="Briefly describe the issue..."
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#E8500A] focus:ring-1 focus:ring-[#E8500A] resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-[#E8500A] hover:bg-[#d14508] text-white font-bold py-3.5 rounded-lg text-sm transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Submit Request — Get Fast Response
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
